import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { listAdapters, createService, updateService } from 'AdminPanel/servicesApi';

/**
 * All-encompassing add/edit for a 3rd-party integration instance. One flow collects:
 *   1) adapter type (from the integrations container catalog) + a unique instance id/name
 *   2) the upstream connection URL + credentials
 *   3) (masking adapters) the per-instance entity -> algorithm map, pre-filled from the adapter's
 *      catalog defaults and overridable
 * It posts everything to the merged /api/admin/services endpoint in one call. Replaces the old
 * split between the admin "Services" screen and the Integrations credentials modal.
 */
function rolesOf(adapter) {
  return adapter && Array.isArray(adapter.roles) ? adapter.roles : [];
}
function isMasking(adapter) {
  return rolesOf(adapter).some((r) => ['MASKING', 'ENCRYPT', 'DECRYPT'].includes(r));
}

function IntegrationWizard({ show, onHide, initial, onSaved, onError }) {
  const isEdit = !!(initial && initial.id);
  const [adapters, setAdapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showMap, setShowMap] = useState(false);

  const [form, setForm] = useState({
    id: '', name: '', adapterVendorId: '',
    baseUrl: '', username: '', password: '',
    insecureTls: false,
    priority: 5, enabled: true,
    entityTypeToAlgorithm: {}, defaultMaskingAlgorithm: '',
    hasStoredPassword: false,
  });

  useEffect(() => {
    if (!show) return;
    setError('');
    setShowMap(false);
    setLoading(true);
    let cancelled = false;
    (async () => {
      const list = await listAdapters();
      if (cancelled) return;
      setAdapters(list);
      if (initial) {
        setForm({
          id: initial.id || '',
          name: initial.name || '',
          adapterVendorId: initial.adapterVendorId || initial.vendor || '',
          baseUrl: initial.baseUrl || '',
          username: initial.username || '',
          password: '',
          insecureTls: initial.insecureTls === true,
          priority: initial.priority || 5,
          enabled: initial.enabled !== false,
          entityTypeToAlgorithm: initial.entityTypeToAlgorithm || {},
          defaultMaskingAlgorithm: initial.defaultMaskingAlgorithm || '',
          hasStoredPassword: !!initial.configured,
        });
      } else {
        const first = list[0];
        setForm((f) => ({
          ...f,
          adapterVendorId: first ? first.vendorId : '',
          entityTypeToAlgorithm: first ? (first.defaultEntityTypeToAlgorithm || {}) : {},
          defaultMaskingAlgorithm: first ? (first.defaultMaskingAlgorithm || '') : '',
        }));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [show, initial]);

  const selectedAdapter = useMemo(
    () => adapters.find((a) => a.vendorId === form.adapterVendorId),
    [adapters, form.adapterVendorId]
  );

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  // When the adapter changes on a fresh create, reset the map defaults to that adapter's.
  const onAdapterChange = (vendorId) => {
    const a = adapters.find((x) => x.vendorId === vendorId);
    setForm((f) => ({
      ...f,
      adapterVendorId: vendorId,
      entityTypeToAlgorithm: a ? (a.defaultEntityTypeToAlgorithm || {}) : {},
      defaultMaskingAlgorithm: a ? (a.defaultMaskingAlgorithm || '') : '',
    }));
  };

  const setMapEntry = (entity, algorithm) =>
    setForm((f) => ({ ...f, entityTypeToAlgorithm: { ...f.entityTypeToAlgorithm, [entity]: algorithm } }));

  const submit = async () => {
    setError('');
    if (!form.adapterVendorId) { setError('Pick an adapter type.'); return; }
    if (!form.id || !form.id.trim()) { setError('Instance id is required.'); return; }
    if (form.id.includes('#')) { setError("Instance id must not contain '#'."); return; }
    if (!form.baseUrl || !form.baseUrl.trim()) { setError('Host is required.'); return; }

    const payload = {
      id: form.id.trim(),
      name: form.name || form.id.trim(),
      adapterVendorId: form.adapterVendorId,
      priority: Number(form.priority) || 5,
      enabled: !!form.enabled,
      baseUrl: form.baseUrl.trim(),
      username: form.username || null,
      // On edit, blank password = keep stored. Omitting the key (undefined is dropped by
      // JSON.stringify) maps to null server-side, which IntegrationConfigService treats as
      // "don't change" — never send a placeholder that could be encrypted as a real secret.
      password: form.password === '' && form.hasStoredPassword ? undefined : form.password,
      insecureTls: !!form.insecureTls,
    };
    if (isMasking(selectedAdapter)) {
      payload.entityTypeToAlgorithm = form.entityTypeToAlgorithm;
      payload.defaultMaskingAlgorithm = form.defaultMaskingAlgorithm || null;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateService(form.id.trim(), payload);
      } else {
        await createService(payload);
      }
      if (onSaved) onSaved();
      onHide();
    } catch (e) {
      setError(e?.message || String(e));
      if (onError) onError(e);
    } finally {
      setSaving(false);
    }
  };

  const mapEntities = Object.keys(form.entityTypeToAlgorithm || {}).sort();

  return (
    <Modal show={show} onHide={onHide} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit integration' : 'Add integration'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className='d-flex justify-content-center p-4'><Spinner animation='border' /></div>
        ) : adapters.length === 0 && !isEdit ? (
          <p className='text-muted'>
            No adapter types are available from the integrations container. Ensure the integrations
            service is deployed and reachable.
          </p>
        ) : (
          <Form onSubmit={(e) => { e.preventDefault(); submit(); }} autoComplete='off'>
            <h6 className='text-muted mb-3'>Service</h6>
            <Row>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Integration type *</Form.Label>
                  <Form.Control as='select' size='sm' data-testid='wiz-adapter'
                    value={form.adapterVendorId} disabled={isEdit}
                    onChange={(e) => onAdapterChange(e.target.value)}>
                    <option value=''>Select…</option>
                    {adapters.map((a) => (
                      <option key={a.vendorId} value={a.vendorId}>{a.displayName || a.vendorId}</option>
                    ))}
                    {isEdit && form.adapterVendorId && !selectedAdapter && (
                      <option value={form.adapterVendorId}>{form.adapterVendorId}</option>
                    )}
                  </Form.Control>
                  {selectedAdapter && (
                    <div className='mt-1'>
                      {rolesOf(selectedAdapter).map((r) => <Badge key={r} variant='info' className='mr-1'>{r}</Badge>)}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Instance id *</Form.Label>
                  <Form.Control size='sm' data-testid='wiz-id' value={form.id} disabled={isEdit}
                    placeholder='delphix-prod' onChange={(e) => set('id', e.target.value)} />
                  <Form.Text className='text-muted'>
                    Identifies the {selectedAdapter ? (selectedAdapter.displayName || selectedAdapter.vendorId) : 'service'} instance and cannot be changed.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className='mb-3'>
              <Form.Label>Display name</Form.Label>
              <Form.Control size='sm' value={form.name} placeholder='Delphix Prod'
                onChange={(e) => set('name', e.target.value)} />
            </Form.Group>

            <h6 className='text-muted mb-3 mt-2'>Connection</h6>
            <Form.Group className='mb-3'>
              <Form.Label>Host *</Form.Label>
              <Form.Control size='sm' data-testid='wiz-baseurl' value={form.baseUrl}
                placeholder={`your-${form.adapterVendorId || 'engine'}-host.example.com`}
                onChange={(e) => set('baseUrl', e.target.value)} />
              <Form.Text className='text-muted'>
                Scheme optional — https is assumed. Just the engine host; the API path is added automatically.
              </Form.Text>
              <Form.Check type='checkbox' id='wiz-insecure-tls' data-testid='wiz-insecure-tls'
                className='mt-2' label='Uses a self-signed certificate (skip TLS certificate verification)'
                checked={form.insecureTls} onChange={(e) => set('insecureTls', e.target.checked)} />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Username</Form.Label>
                  <Form.Control size='sm' value={form.username} autoComplete='new-password'
                    onChange={(e) => set('username', e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Password {form.hasStoredPassword ? '' : ''}</Form.Label>
                  <Form.Control size='sm' type='password' data-testid='wiz-password' value={form.password}
                    placeholder={form.hasStoredPassword ? '(leave blank to keep stored password)' : ''}
                    autoComplete='new-password' onChange={(e) => set('password', e.target.value)} />
                </Form.Group>
              </Col>
            </Row>

            {isMasking(selectedAdapter) && (
              <>
                <div className='d-flex align-items-center mt-2 mb-2'>
                  <h6 className='text-muted mb-0 flex-grow-1'>Masking algorithms</h6>
                  <Button variant='link' size='sm' className='p-0'
                    onClick={() => setShowMap((s) => !s)}>
                    {showMap ? 'Hide' : `Edit map (${mapEntities.length})`}
                  </Button>
                </div>
                {showMap && (
                  <>
                    <Form.Group className='mb-2'>
                      <Form.Label className='small'>Default algorithm (fallback)</Form.Label>
                      <Form.Control size='sm' value={form.defaultMaskingAlgorithm}
                        onChange={(e) => set('defaultMaskingAlgorithm', e.target.value)} />
                    </Form.Group>
                    <div style={{ maxHeight: 220, overflowY: 'auto' }} className='border rounded p-2'>
                      {mapEntities.map((entity) => (
                        <Row key={entity} className='align-items-center mb-1'>
                          <Col xs={5}><span className='small text-monospace'>{entity}</span></Col>
                          <Col xs={7}>
                            <Form.Control size='sm' value={form.entityTypeToAlgorithm[entity]}
                              onChange={(e) => setMapEntry(entity, e.target.value)} />
                          </Col>
                        </Row>
                      ))}
                    </div>
                    <Form.Text className='text-muted'>
                      Pre-filled from the adapter's defaults. Override any entity's algorithm for this instance.
                    </Form.Text>
                  </>
                )}
              </>
            )}

            {/* Priority is intentionally not exposed: C2's own services stay the default provider.
                Choosing a different provider for a specific data source sets that source's preference
                (per-data-source), not a global ranking — so an admin priority knob would mislead. */}
            <Row className='mt-3'>
              <Col className='d-flex align-items-end'>
                <Form.Check type='switch' id='wiz-enabled' label='Enabled' className='mb-1'
                  checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
              </Col>
            </Row>
            {error ? <div className='text-danger small mt-3'>{error}</div> : null}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant='primary' onClick={submit} disabled={saving || loading || (adapters.length === 0 && !isEdit)}>
          {saving ? <Spinner as='span' size='sm' animation='border' className='mr-2' /> : null}
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default IntegrationWizard;
